/**
 * Единый источник высоты приложения и safe-area insets.
 * Решает: 100vh vs реальная высота (TG/VK/мобильный браузер) и обрезку topbar в Telegram Web.
 */

export type ViewportInsets = { top?: number; right?: number; bottom?: number; left?: number };

/** Согласовано с Modal: «клавиатура» = заметное сжатие visualViewport относительно innerHeight. */
export const KEYBOARD_VIEWPORT_DELTA_PX = 120;

/**
 * Глобальный режим клавиатуры: класс + CSS vars на documentElement.
 * ScreenLayout читает --screen-scroll-* в inline (иначе индексный CSS проигрывает каскаду).
 * Ничего не прячем: нижняя панель как обычно; скролл-хост укладывается в visualViewport (над клавиатурой).
 */
export function syncKeyboardViewportMode(): void {
  if (typeof document === 'undefined' || !document.documentElement) return;
  const root = document.documentElement;

  const clearScrollLayoutVars = () => {
    root.style.removeProperty('--screen-scroll-top');
    root.style.removeProperty('--screen-scroll-bottom');
    root.style.removeProperty('--screen-scroll-height');
    root.style.removeProperty('--screen-content-min-height');
  };

  if (typeof window === 'undefined' || !window.visualViewport) {
    root.classList.remove('app-keyboard-open');
    root.style.removeProperty('--visual-viewport-height');
    root.style.removeProperty('--visual-viewport-offset-top');
    clearScrollLayoutVars();
    return;
  }

  const vv = window.visualViewport;
  const vvh = Math.round(vv.height);
  const vvo = Math.round(vv.offsetTop);
  const ih = window.innerHeight;
  const keyboardOpen = ih - vvh > KEYBOARD_VIEWPORT_DELTA_PX;

  root.style.setProperty('--visual-viewport-height', `${vvh}px`);
  root.style.setProperty('--visual-viewport-offset-top', `${vvo}px`);

  if (keyboardOpen) {
    root.classList.add('app-keyboard-open');
    root.style.setProperty(
      '--screen-scroll-top',
      'calc(var(--visual-viewport-offset-top) + var(--screen-top-offset))'
    );
    root.style.setProperty('--screen-scroll-bottom', 'auto');
    root.style.setProperty(
      '--screen-scroll-height',
      'calc(var(--visual-viewport-height) - var(--screen-top-offset))'
    );
    root.style.setProperty('--screen-content-min-height', '0');
  } else {
    root.classList.remove('app-keyboard-open');
    clearScrollLayoutVars();
  }
}

export function applyViewportVars(opts?: { height?: number; insets?: ViewportInsets }): void {
  if (typeof document === 'undefined' || !document.documentElement) return;

  const root = document.documentElement.style;

  /** Только insets от VK — не трогаем --app-height (иначе затираем высоту от VKWebAppGetConfig). */
  const insetsOnly =
    opts !== undefined && opts.insets !== undefined && opts.height === undefined;

  if (!insetsOnly) {
    const height =
      opts?.height != null && opts.height > 0
        ? opts.height
        : typeof window !== 'undefined' && window.visualViewport?.height
          ? Math.round(window.visualViewport.height)
          : typeof window !== 'undefined'
            ? window.innerHeight
            : 0;
    if (height > 0) {
      root.setProperty('--app-height', `${height}px`);
    }
  }

  const ins = opts?.insets ?? {};
  const top = ins.top ?? 0;
  const right = ins.right ?? 0;
  const bottom = ins.bottom ?? 0;
  const left = ins.left ?? 0;

  root.setProperty('--safe-top', `${top}px`);
  root.setProperty('--safe-right', `${right}px`);
  root.setProperty('--safe-bottom', `${bottom}px`);
  root.setProperty('--safe-left', `${left}px`);
  root.setProperty('--app-safe-area-inset-top', `${top}px`);
  root.setProperty('--app-safe-area-inset-bottom', `${bottom}px`);

  syncKeyboardViewportMode();
}

/**
 * Высота видимого вьюпорта: TG viewportHeight → VK → visualViewport → innerHeight.
 * Вызывать при загрузке, resize, viewportChanged (TG), VKWebAppUpdateConfig (VK).
 */
export function setAppHeight(overrideHeight?: number): void {
  if (typeof document === 'undefined' || !document.documentElement) return;

  let h: number;
  if (overrideHeight != null && overrideHeight > 0) {
    h = overrideHeight;
  } else {
    const tg = window.Telegram?.WebApp;
    const tgH = typeof tg?.viewportHeight === 'number' ? tg.viewportHeight : 0;
    h =
      tgH > 0
        ? tgH
        : typeof window.visualViewport?.height === 'number'
          ? Math.round(window.visualViewport.height)
          : window.innerHeight;
  }
  document.documentElement.style.setProperty('--app-height', `${h}px`);
  syncKeyboardViewportMode();
}
